import { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Wire up the worker from a CDN matching the installed pdfjs-dist version
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/**
 * PdfCanvas — renders a PDF page with annotation overlay.
 * Props:
 *   fileUrl: data URL or http URL of the PDF
 *   comments: array of comments with `annotation` (each annotation may include `page` (1-indexed))
 *   readOnly: bool — disable creating new annotations
 *   activeTool: "pin" | "rect"
 *   onCreateAnnotation: ({type, x, y, w, h, page}) => void
 *   selectedCommentId, setSelectedCommentId
 *   pendingAnno: live in-progress annotation to draw
 *   testidPrefix: e.g. "anno" or "public-anno"
 */
export default function PdfCanvas({
  fileUrl,
  comments,
  readOnly = false,
  activeTool = "pin",
  onCreateAnnotation,
  selectedCommentId,
  setSelectedCommentId,
  testidPrefix = "anno",
  pendingAnno = null,
}) {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [dragRect, setDragRect] = useState(null);
  const [rectStart, setRectStart] = useState(null);
  const pageWrapRef = useRef(null);

  // Reset to first page when file changes
  useEffect(() => { setPage(1); }, [fileUrl]);

  const handleClick = (e) => {
    if (readOnly || activeTool !== "pin") return;
    if (dragRect) return;
    const rect = pageWrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (x < 0 || x > 100 || y < 0 || y > 100) return;
    onCreateAnnotation && onCreateAnnotation({ type: "pin", x, y, page });
  };

  const handleMouseDown = (e) => {
    if (readOnly || activeTool !== "rect") return;
    const rect = pageWrapRef.current.getBoundingClientRect();
    setRectStart({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };
  const handleMouseMove = (e) => {
    if (!rectStart) return;
    const rect = pageWrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setDragRect({
      type: "rect",
      x: Math.min(rectStart.x, x),
      y: Math.min(rectStart.y, y),
      w: Math.abs(x - rectStart.x),
      h: Math.abs(y - rectStart.y),
      page,
    });
  };
  const handleMouseUp = () => {
    if (rectStart && dragRect && (dragRect.w > 1 || dragRect.h > 1)) {
      onCreateAnnotation && onCreateAnnotation(dragRect);
    }
    setDragRect(null);
    setRectStart(null);
  };

  // Filter comments to current page (legacy comments without a page → show on page 1)
  const pageComments = comments.filter((c) => {
    if (!c.annotation) return false;
    const cp = c.annotation.page || 1;
    return cp === page;
  });

  return (
    <div className="w-full" data-testid="pdf-canvas-root">
      {/* Page controls */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-3 mb-3" data-testid="pdf-page-controls">
          <Button size="sm" variant="outline" className="rounded-none h-7"
            onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="pdf-prev-page-btn">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <span className="text-xs font-mono" data-testid="pdf-page-indicator">Page {page} / {numPages}</span>
          <Button size="sm" variant="outline" className="rounded-none h-7"
            onClick={() => setPage((p) => Math.min(numPages, p + 1))} disabled={page >= numPages} data-testid="pdf-next-page-btn">
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      <Document
        file={fileUrl}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        loading={<div className="text-muted-foreground text-center py-10">Loading PDF…</div>}
        error={<div className="text-red-500 text-center py-10">Failed to load PDF.</div>}
      >
        <div
          ref={pageWrapRef}
          className={`relative inline-block ${readOnly ? "" : "cursor-crosshair"} select-none`}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          data-testid={`${testidPrefix}-pdf-page-wrap`}
        >
          <Page pageNumber={page} width={900} renderAnnotationLayer={false} renderTextLayer={false} />

          {/* existing annotations on this page */}
          {pageComments.map((c) => {
            const isSel = selectedCommentId === c.id;
            const num = comments.indexOf(c) + 1;
            if (c.annotation.type === "pin") {
              return (
                <div
                  key={c.id}
                  className={`anno-pin ${c.status === "resolved" ? "resolved" : ""}`}
                  style={{ left: `${c.annotation.x}%`, top: `${c.annotation.y}%`, outline: isSel ? "2px solid white" : undefined }}
                  onClick={(e) => { e.stopPropagation(); setSelectedCommentId && setSelectedCommentId(c.id); }}
                  data-testid={`${testidPrefix}-pin-${c.id}`}
                >
                  {num}
                </div>
              );
            }
            if (c.annotation.type === "rect") {
              return (
                <div
                  key={c.id}
                  className="absolute border-2 border-primary bg-primary/10 cursor-pointer"
                  style={{
                    left: `${c.annotation.x}%`, top: `${c.annotation.y}%`,
                    width: `${c.annotation.w}%`, height: `${c.annotation.h}%`,
                  }}
                  onClick={(e) => { e.stopPropagation(); setSelectedCommentId && setSelectedCommentId(c.id); }}
                  data-testid={`${testidPrefix}-rect-${c.id}`}
                />
              );
            }
            return null;
          })}

          {/* live rect while user is dragging */}
          {dragRect?.type === "rect" && (
            <div
              className="anno-rect pending absolute pointer-events-none"
              style={{ left: `${dragRect.x}%`, top: `${dragRect.y}%`, width: `${dragRect.w}%`, height: `${dragRect.h}%` }}
            />
          )}
          {/* pending rect being drawn */}
          {pendingAnno?.type === "rect" && pendingAnno?.page === page && (
            <div
              className="anno-rect pending absolute pointer-events-none"
              style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%`, width: `${pendingAnno.w}%`, height: `${pendingAnno.h}%` }}
              data-testid={`${testidPrefix}-rect-pending`}
            />
          )}
          {/* pending pin shown while the comment draft is open */}
          {pendingAnno?.type === "pin" && pendingAnno?.page === page && (
            <div
              className="anno-pin pending"
              style={{ left: `${pendingAnno.x}%`, top: `${pendingAnno.y}%` }}
              data-testid={`${testidPrefix}-pin-pending`}
            >
              +
            </div>
          )}
        </div>
      </Document>
    </div>
  );
}
